/* eslint react/prop-types: off */
import React from 'react'
import { every, includes, isNil, get, find } from 'lodash'
import moment from 'moment'

import { cloudhelpGet } from 'Utilities/CloudHelp'
import PopupWrapper from 'Components/PopupWrapper/PopupWrapper'

import PriorityCell from '../PriorityCell'
import Tags from '../Tags'

const StatusPill = ({ status, parentStatus, lastComment }) => {
  let pillColor = 'error'
  let lastResponseItems = <li>No response</li>

  if (!isNil(lastComment)) {
    pillColor = lastComment.client ? 'orange' : 'green'

    if (includes(['Resolved', 'Rejected'], parentStatus)) {
      pillColor = 'gray'
    }

    lastResponseItems = (
      <>
        <li>
          {`Last reply from: ${lastComment.user_name} (${lastComment.client ? 'Client' : 'Calligo'})`}
        </li>

        <li>
          {`Last reply at: ${moment(lastComment.created_at).format('DD MMM YYYY, hh:mmA')}`}
        </li>
      </>
    )
  }

  const statusPill = <div className={`badge badge--${pillColor}`}>{status}</div>

  return (
    <PopupWrapper
      trigger={statusPill}
      options={{
        on: 'hover',
        contentStyle: {
          padding: '0 20px',
          border: 'none',
          width: 'auto',
          backgroundColor: 'transparent',
          boxShadow: 'none'
        }
      }}
    >
      <ul className="list list--no-list-style">
        <li>
          {`Status: ${status}`}
        </li>
        {lastResponseItems}
      </ul>
    </PopupWrapper>
  )
}

const AlertCell = ({ alert }) => (
  <PopupWrapper
    trigger={<span className="lnr lnr-warning lnr--left-spaced" />}
  >
    <div>
      Alert
      <a href={`/alerts/${alert.id}`} className="link--decorated">
        {` #${alert.id}`}
      </a>
    </div>
  </PopupWrapper>
)

const ProblemCell = ({ problem_id: problemID }) => (
  <PopupWrapper
    trigger={<span className="lnr lnr-alarm-ringing lnr--left-spaced" />}
  >
    <div>
      Problem
      <a href={`/problems/${problemID}`} className="link--decorated">
        {` #${problemID}`}
      </a>
    </div>
  </PopupWrapper>
)

const SecondaryTicketCell = ({ primary_ticket_id: ticketID }) => (
  <PopupWrapper
    trigger={<span className="lnr lnr-arrows-merge lnr--left-spaced" />}
  >
    <div>
      Merged into
      <a href={`/tickets/${ticketID}`} className="link--decorated">
        {` #${ticketID}`}
      </a>
    </div>
  </PopupWrapper>
)

const childCell = (value) => {
  if (every([value.problem_id, value.primary_ticket_id], isNil)) {
    return ('No')
  }

  return (
    <>
      {value.problem_id && <ProblemCell problem_id={value.problem_id} />}
      {value.primary_ticket_id && <SecondaryTicketCell primary_ticket_id={value.primary_ticket_id} />}
    </>
  )
}

const ClientCell = ({ client }) => {
  let className = ''

  if (client?.clientStatus === 'historic') className = 'text-red-400'
  else if (client?.clientStatus === 'pending_offboarding') className = 'text-yellow-600'

  return <div className={className}>{client.client_name}</div>
}

const Columns = ({ clients, employee }) => {
  const ticketTags = cloudhelpGet('/api/ticket_tags').then(res => (res.data))

  return (
    [
      {
        text: '',
        accessor: 'checkbox',
        type: 'row_checkbox',
        defaultWidth: '30',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'In response SLA',
        accessor: 'sla_response_color',
        className: 'flex flex--centered',
        type: 'dot',
        defaultWidth: '75',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'In resolution SLA',
        accessor: 'sla_resolution_color',
        className: 'flex flex--centered',
        type: 'dot',
        defaultWidth: '75',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'ID',
        accessor: 'id',
        defaultWidth: '100',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Title',
        accessor: 'title',
        defaultWidth: '350',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Priority',
        accessor: 'priority_id',
        className: 'flex flex--centered',
        Cell: row => (
          <PriorityCell
            ticket={row.original}
            keyClient={get(find(clients, ['id', row.original.client_id]), 'key_client', false)}
          />
        ),
        defaultWidth: '75',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Client',
        accessor: 'client_name',
        defaultWidth: '125',
        enabled: true,
        employeeOnly: false,
        Cell: row => <ClientCell client={row.original} />
      },
      {
        text: 'Tags',
        accessor: 'tags',
        defaultWidth: '100',
        Cell: row => <Tags key={row.original.id.value} ticketID={row.original.id.value} ticketTags={ticketTags}/>,
        disableHref: true,
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Queue',
        accessor: 'queue_name',
        defaultWidth: '100',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Assignee',
        accessor: 'assignee_name',
        defaultWidth: '100',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Status',
        accessor: 'status_text',
        defaultWidth: '100',
        Cell: (row) => {
          if (employee) {
            return (
              <StatusPill
                status={row.value}
                parentStatus={row.original.parent_status}
                lastComment={row.original.lastComment}
              />
            )
          }
          return row.value
        },
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Category',
        accessor: 'service_category_name',
        defaultWidth: '120',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Security Issue',
        accessor: 'securityIssue',
        defaultWidth: '120',
        enabled: true,
        employeeOnly: false,
        Cell: row => <div>{row.original.securityIssue ? "Yes" : "No"}</div>
      },
      {
        text: 'Time Logged',
        accessor: 'timesheetsDuration',
        defaultWidth: '100',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Contact',
        accessor: 'contact_name',
        defaultWidth: '100',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Child?',
        accessor: 'child',
        className: 'flex flex--centered',
        Cell: row => childCell(row.value),
        defaultWidth: '50',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Alert',
        accessor: 'alert',
        className: 'flex flex--centered',
        Cell: row => (isNil(row.value) ? 'No' : <AlertCell alert={row.value} />),
        defaultWidth: '50',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Created',
        accessor: 'created_at',
        Cell: row => (moment(row.value).format('DD MMM YYYY, hh:mmA')),
        defaultWidth: '50',
        enabled: true,
        employeeOnly: false
      },
      {
        text: 'Last Updated',
        accessor: 'lastUpdated',
        Cell: row => (
          row.value ? moment(row.value).format('DD MMM YYYY, hh:mmA') : 'No correspondence'
        ),
        defaultWidth: '75',
        enabled: true,
        employeeOnly: true
      },
      {
        text: 'Resolved At',
        accessor: 'resolvedAt',
        Cell: row => (
          row.value ? moment(row.value).format('DD MMM YYYY, hh:mmA') : 'Not yet resolved'
        ),
        defaultWidth: '75',
        enabled: true,
        employeeOnly: false
      }
    ])
}

export default Columns
